/**
 * Demo Data: Recurring Transactions
 *
 * Pre-populated recurring transaction templates for BuildRight Construction Ltd
 */

import { RecurringTransaction } from "../types/recurring-transaction";

// Helper function to calculate next due date based on frequency
function calculateNextDueDate(startDate: Date, frequency: string): Date {
  const today = new Date(); // Always use current date
  today.setHours(0, 0, 0, 0);
  const next = new Date(startDate);

  // Move forward until we find the next occurrence after today
  while (next <= today) {
    switch (frequency) {
      case "Daily":
        next.setDate(next.getDate() + 1);
        break;
      case "Weekly":
        next.setDate(next.getDate() + 7);
        break;
      case "Fortnightly":
        next.setDate(next.getDate() + 14);
        break;
      case "Monthly":
        next.setMonth(next.getMonth() + 1);
        break;
      case "Quarterly":
        next.setMonth(next.getMonth() + 3);
        break;
      case "Annual":
        next.setFullYear(next.getFullYear() + 1);
        break;
    }
  }

  return next;
}

export const demoRecurringTransactions: RecurringTransaction[] = [
  {
    id: "rt-001",
    name: "PAYE & National Insurance",
    amount: 12847.63,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-22"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-22"), "Monthly"),
    category: "Payroll",
    status: "Active",
    notes:
      "Monthly PAYE and National Insurance contributions due 22nd of each month",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-002",
    name: "VAT Payment",
    amount: 19347.2,
    isVariable: false,
    frequency: "Quarterly",
    startDate: new Date("2025-04-07"),
    nextDueDate: calculateNextDueDate(new Date("2025-04-07"), "Quarterly"),
    category: "Tax",
    status: "Active",
    notes: "Quarterly VAT payment due 7th of month following quarter end",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-003",
    name: "Corporation Tax",
    amount: 44718.5,
    isVariable: false,
    frequency: "Annual",
    startDate: new Date("2025-01-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-01"), "Annual"),
    category: "Tax",
    status: "Active",
    notes: "Annual corporation tax payment",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-004",
    name: "Office Rent",
    amount: 3475.0,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-01"), "Monthly"),
    category: "Facilities",
    status: "Active",
    notes: "Monthly office rent payment due 1st of each month",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-005",
    name: "Business Insurance",
    amount: 2318.44,
    isVariable: false,
    frequency: "Annual",
    startDate: new Date("2025-05-15"),
    nextDueDate: calculateNextDueDate(new Date("2025-05-15"), "Annual"),
    category: "Operating Expenses",
    status: "Active",
    notes: "Annual business insurance premium",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-006",
    name: "Utilities - Electric",
    amount: null,
    isVariable: true,
    frequency: "Monthly",
    startDate: new Date("2025-01-15"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-15"), "Monthly"),
    category: "Facilities",
    status: "Active",
    notes: "Monthly electricity bill - amount varies by usage",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-007",
    name: "Software Subscriptions",
    amount: 847.35,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-10"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-10"), "Monthly"),
    category: "Operating Expenses",
    status: "Active",
    notes:
      "Monthly software and SaaS subscriptions (project management, accounting, etc.)",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-008",
    name: "Vehicle Lease Payments",
    amount: 1184.67,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-05"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-05"), "Monthly"),
    category: "Operating Expenses",
    status: "Active",
    notes:
      "Monthly lease payments for company vehicles (3x Ford Transit, 1x Ranger)",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-009",
    name: "Equipment Maintenance",
    amount: 623.75,
    isVariable: false,
    frequency: "Quarterly",
    startDate: new Date("2025-03-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-03-01"), "Quarterly"),
    category: "Operating Expenses",
    status: "Active",
    notes: "Quarterly equipment servicing and maintenance contract",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-010",
    name: "Professional Memberships",
    amount: 467.5,
    isVariable: false,
    frequency: "Annual",
    startDate: new Date("2025-06-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-06-01"), "Annual"),
    category: "Operating Expenses",
    status: "Active",
    notes: "Annual professional body memberships — CIBSE, CIOB, ICE",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-011",
    name: "Subcontractor Retentions Release",
    amount: 8340.0,
    isVariable: false,
    frequency: "Quarterly",
    startDate: new Date("2025-03-31"),
    nextDueDate: calculateNextDueDate(new Date("2025-03-31"), "Quarterly"),
    category: "Subcontractors",
    status: "Active",
    notes: "Quarterly release of 5% retention held on subcontractor payments",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-012",
    name: "Site Safety & Compliance",
    amount: 294.17,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-01"), "Monthly"),
    category: "Operating Expenses",
    status: "Active",
    notes: "Monthly H&S compliance subscription and toolbox talk platform",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-013",
    name: "Plant & Equipment Insurance",
    amount: 1876.25,
    isVariable: false,
    frequency: "Annual",
    startDate: new Date("2025-04-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-04-01"), "Annual"),
    category: "Facilities",
    status: "Active",
    notes:
      "Annual plant and equipment insurance — JCB, telehandler, scaffold stock",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-014",
    name: "Pension Contributions",
    amount: 3218.94,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-28"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-28"), "Monthly"),
    category: "Payroll",
    status: "Active",
    notes: "Monthly employer pension contributions — auto-enrolment scheme",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-01-01"),
  },
  {
    id: "rt-015",
    name: "Waste Disposal Contract",
    amount: 412.8,
    isVariable: false,
    frequency: "Monthly",
    startDate: new Date("2025-01-01"),
    nextDueDate: calculateNextDueDate(new Date("2025-01-01"), "Monthly"),
    category: "Facilities",
    status: "Paused",
    notes:
      "Monthly skip hire and waste disposal — paused while Midlands JV site is inactive",
    dataSourceName: "Manual Entry - Recurring Commitments",
    createdAt: new Date("2025-01-01"),
    updatedAt: new Date("2025-03-01"),
  },
];

// SOURCE PROVENANCE — all recurring transactions originate from manual entry
// on the Recurring Transactions screen. This is stamped at the data layer
// so every consumer (detail view, drill-downs, exports) gets it for free.
const RECURRING_TRANSACTION_SOURCE = "Manual Entry - Recurring Commitments";

// Get all recurring transactions
export function getRecurringTransactions(): RecurringTransaction[] {
  return demoRecurringTransactions.map((rt) => ({
    ...rt,
    dataSourceName: RECURRING_TRANSACTION_SOURCE,
  }));
}

// Get active recurring transactions only
export function getActiveRecurringTransactions(): RecurringTransaction[] {
  return getRecurringTransactions().filter((rt) => rt.status === "Active");
}

// Get recurring transactions by category
export function getRecurringTransactionsByCategory(
  category: string,
): RecurringTransaction[] {
  return demoRecurringTransactions.filter((rt) => rt.category === category);
}

// Get unique categories
export function getRecurringTransactionCategories(): string[] {
  const categories = new Set(
    demoRecurringTransactions.map((rt) => rt.category),
  );
  return Array.from(categories).sort();
}
